import {
  selectBuildingsDamage,
  selectCurrentPlayer,
  selectHexes,
  selectPlayers,
  selectSpecialBuildings,
  selectUnits,
} from "../redux/reducers/sharedStateReducer/selectors";
import {getGuildsAndTemplesWithChangedIncome} from "../utils/gameState/income/getGuildsAndTemplesWithChangedIncome";
import {calculateCitiesCache} from "../utils/gameState/calculateCitiesCache";
import {
  getPlannedGuildsAndTemplesIncomeChange
} from "../utils/gameState/construction/planning/getPlannedGuildsAndTemplesIncomeChange";
import {checkIfConstructionPlanningAvailable} from "../utils/gameState/construction/planning/getConstructionPlanContext";
import {selectConstructionPlanEntries} from "../redux/reducers/privateStateReducer/selectors";

// Задержка перед проверкой соседних гильдий/храмов совпадает с задержкой
// показа дохода только что построенной гильдии/храма (см. вызовы
// showSpecialBuildingIncomeAnimation при SET_SPECIAL_BUILDING): даёт время
// новому токену отрисоваться на сцене и не накладывает звук раскрытия дохода
// на звук самой постройки
export const NEARBY_INCOME_CHANGE_DELAY_MS = 1000;
// Если затронуто сразу несколько гильдий/храмов — их звук/анимация идут не
// одновременно, а внахлест с небольшим сдвигом, чтобы оставаться различимыми
export const AFFECTED_BUILDING_REVEAL_STAGGER_MS = 250;

/**
 * После размещения строения на placedHexKey проверяет все гильдии/храмы
 * (любого игрока) в радиусе видимости дохода и для тех, чей доход изменился,
 * проигрывает ту же разовую анимацию путей влияния/дохода и тот же звук, что
 * при инспекции такой ячейки (см. GameScene.handleInspectionClick).
 *
 * hexesBefore нужен нетронутым, каким он был ДО диспатча размещающего
 * действия — только SET_HEX_TOKEN меняет hexes и тем самым может повлиять на
 * чужой доход (специальные строения кладутся в отдельный слой specialBuildings
 * и на пути влияния не воздействуют).
 *
 * citiesCache из стора здесь не годится: gameState.citiesCache пересчитывается
 * только на COMPLETE_PHASE (конец хода игрока), поэтому в течение одного хода
 * строительства он не видит города/кварталы, расширенные более ранними
 * действиями этого же хода — гильдия соседа могла получить доступ именно
 * благодаря такому расширению. Пересчитываем citiesCache "по требованию" из
 * актуальных hexesBefore/hexesAfter вместо чтения стора.
 *
 * Кто и что затронуто считаем СИНХРОННО, сразу по возврату из dispatch — а не
 * внутри setTimeout. Диспатч синхронный, так что store.getState() здесь уже
 * гарантированно отражает именно ЭТО размещение и ничего позже него. Если бы
 * hexesAfter читался из стора только внутри отложенного колбэка, и игрок успел
 * бы поставить второе строение до истечения задержки первого — колбэк первого
 * действия увидел бы в сторе уже ОБА строения и ошибочно приписал бы себе
 * изменение дохода, которое на самом деле создало только второе действие
 * (сиквенс запускался бы дважды с одинаковым, по сути принадлежащим только
 * второму действию, значением дохода). setTimeout ниже управляет только
 * моментом ПРОИГРЫВАНИЯ (даёт звуку/спрайту постройки не наложиться на
 * раскрытие дохода) — не тем, произошло ли изменение и каким оно было.
 */
export const playNearbyGuildsTemplesIncomeChangeAnimation = (
  store, soundManager, hexGridRenderer, placedHexKey, hexesBefore, citiesCaches, planEntriesBefore
) => {
  if (!soundManager || !hexGridRenderer || !hexesBefore) return;

  const state = store.getState();
  const hexesAfter = selectHexes(state);
  const specialBuildings = selectSpecialBuildings(state);
  const players = selectPlayers(state);
  const units = selectUnits(state);
  const buildingsDamage = selectBuildingsDamage(state);
  const currentPlayer = selectCurrentPlayer(state);

  // Пара кэшей может прийти уже посчитанной от playBuildingPlacementEffects —
  // размещение жетона запускает две анимации, и каждая считала бы ту же пару
  // заново (полный обход поля на каждый вызов)
  const citiesCacheBefore = citiesCaches
    ? citiesCaches.citiesCacheBefore : calculateCitiesCache(hexesBefore, specialBuildings);
  const citiesCacheAfter = citiesCaches
    ? citiesCaches.citiesCacheAfter : calculateCitiesCache(hexesAfter, specialBuildings);

  const affected = getGuildsAndTemplesWithChangedIncome(
    placedHexKey, hexesBefore, hexesAfter, specialBuildings, players, citiesCacheBefore, citiesCacheAfter
  );

  const reveals = affected.map(({hexKey, buildingType}) => () => {
    soundManager.playSpecialBuildingPlacement(buildingType);
    hexGridRenderer.showSpecialBuildingIncomeAnimation(
      hexKey, buildingType, players, citiesCacheAfter, units, hexesAfter, specialBuildings, buildingsDamage, currentPlayer
    );
  });

  // Запланированные гильдии и храмы планирующего игрока (чужой ход фазы
  // строительства): чужая стройка меняет их доход так же, как у построенных, —
  // и анимация та же, только на поле «с планом» и от лица самого игрока.
  // planEntriesBefore — план до чужой стройки: она могла снять его часть
  const planned = getPlannedGuildsAndTemplesIncomeChange(state, placedHexKey, hexesBefore, planEntriesBefore);
  if (planned) {
    planned.affected.forEach(({hexKey, buildingType}) => {
      reveals.push(() => {
        // Показ отложен, а за это время запланированное могло сняться: самим
        // игроком, кварталом соперника рядом, началом своего хода. Построенные
        // так не исчезают, а запланированные — да
        const stateAtReveal = store.getState();
        const isStillPlanned = checkIfConstructionPlanningAvailable(stateAtReveal)
          && selectConstructionPlanEntries(stateAtReveal)
            .some((entry) => entry.hexKey === hexKey && entry.token === buildingType);
        if (!isStillPlanned) {
          return;
        }
        soundManager.playSpecialBuildingPlacement(buildingType);
        hexGridRenderer.showSpecialBuildingIncomeAnimation(
          hexKey, buildingType, players, planned.citiesCache, units, planned.hexes, planned.specialBuildings,
          buildingsDamage, planned.player
        );
      });
    });
  }

  reveals.forEach((reveal, index) => {
    setTimeout(reveal, NEARBY_INCOME_CHANGE_DELAY_MS + index * AFFECTED_BUILDING_REVEAL_STAGGER_MS);
  });
};
